/**
 * DrumSynth — procedural drum sounds, synthesized directly into AudioBuffers
 * (no sample files, no licensing concerns). Buffers are generated once per
 * sound and cached.
 */

export type DrumSound =
  | 'kick'
  | 'rim'
  | 'snare'
  | 'clap'
  | 'chh'
  | 'ohh'
  | 'tomL'
  | 'tomH';

/** MIDI note → drum sound (GM-ish layout). */
export const DRUM_NOTES: Record<number, DrumSound> = {
  36: 'kick',
  37: 'rim',
  38: 'snare',
  39: 'clap',
  41: 'tomH',
  43: 'tomL',
  45: 'tomL',
  47: 'tomH',
  42: 'chh',
  46: 'ohh',
};

/** Ordered rows for the pattern editor (drum channels). */
export const DRUM_ROWS: Array<{ note: number; label: string }> = [
  { note: 36, label: 'Kick' },
  { note: 37, label: 'Rim' },
  { note: 38, label: 'Snare' },
  { note: 39, label: 'Clap' },
  { note: 41, label: 'Tom H' },
  { note: 43, label: 'Tom L' },
  { note: 42, label: 'H.Hat' },
  { note: 46, label: 'O.Hat' },
];

const SR = 44100;

function buffer(lengthSec: number): { buf: AudioBuffer; data: Float32Array } {
  // Built against a throwaway OfflineAudioContext for a valid AudioBuffer
  // with the right sample rate; we fill the channel data ourselves.
  const buf = new OfflineAudioContext(1, Math.ceil(lengthSec * SR), SR).createBuffer(
    1,
    Math.ceil(lengthSec * SR),
    SR,
  );
  return { buf, data: buf.getChannelData(0) };
}

function normalize(data: Float32Array, peak = 0.85): void {
  let max = 0;
  for (let i = 0; i < data.length; i++) max = Math.max(max, Math.abs(data[i]));
  if (max > 0) {
    const k = peak / max;
    for (let i = 0; i < data.length; i++) data[i] *= k;
  }
}

/** One-pole high-pass, in place. */
function highPass(data: Float32Array, fc: number): void {
  const rc = 1 / (2 * Math.PI * fc);
  const dt = 1 / SR;
  const alpha = rc / (rc + dt);
  let yPrev = 0;
  let xPrev = 0;
  for (let i = 0; i < data.length; i++) {
    const x = data[i];
    const y = alpha * (yPrev + x - xPrev);
    data[i] = y;
    yPrev = y;
    xPrev = x;
  }
}

function makeKick(): AudioBuffer {
  const { buf, data } = buffer(0.5);
  let phase = 0;
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    const f = 45 + 115 * Math.exp(-t * 28);
    phase += (2 * Math.PI * f) / SR;
    const body = Math.sin(phase) * Math.exp(-t * 6.5);
    const click = t < 0.004 ? (Math.random() * 2 - 1) * Math.exp(-t * 900) * 0.6 : 0;
    data[i] = body * 0.95 + click;
  }
  normalize(data);
  return buf;
}

function makeSnare(): AudioBuffer {
  const { buf, data } = buffer(0.35);
  let phase = 0;
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    const noise = (Math.random() * 2 - 1) * Math.exp(-t * 21);
    phase += (2 * Math.PI * 190) / SR;
    const tone = Math.sin(phase) * Math.exp(-t * 38) * 0.5;
    data[i] = noise + tone;
  }
  highPass(data, 1400);
  // Keep a little body back in after the high-pass.
  let bodyPhase = 0;
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    bodyPhase += (2 * Math.PI * 190) / SR;
    data[i] += Math.sin(bodyPhase) * Math.exp(-t * 30) * 0.35;
  }
  normalize(data);
  return buf;
}

function makeClap(): AudioBuffer {
  const { buf, data } = buffer(0.4);
  const burst = (x: number) => (x > 0 ? Math.exp(-x * 55) : 0);
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    const env =
      burst(t) +
      0.75 * burst(t - 0.012) +
      0.55 * burst(t - 0.024) +
      0.4 * (t > 0.024 ? Math.exp(-(t - 0.024) * 11) : 0);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  highPass(data, 1100);
  normalize(data);
  return buf;
}

function makeHat(fc: number, decay: number, length: number): AudioBuffer {
  const { buf, data } = buffer(length);
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    data[i] = (Math.random() * 2 - 1) * Math.exp(-t * decay);
  }
  highPass(data, fc);
  normalize(data);
  return buf;
}

function makeRim(): AudioBuffer {
  const { buf, data } = buffer(0.08);
  let p1 = 0;
  let p2 = 0;
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    p1 += (2 * Math.PI * 790) / SR;
    p2 += (2 * Math.PI * 1580) / SR;
    data[i] = (Math.sin(p1) + 0.5 * Math.sin(p2)) * Math.exp(-t * 95);
  }
  normalize(data);
  return buf;
}

function makeTom(f0: number): AudioBuffer {
  const { buf, data } = buffer(0.45);
  let phase = 0;
  for (let i = 0; i < data.length; i++) {
    const t = i / SR;
    const f = f0 * Math.pow(0.55, t / 0.3);
    phase += (2 * Math.PI * f) / SR;
    data[i] = Math.sin(phase) * Math.exp(-t * 9);
  }
  normalize(data);
  return buf;
}

export class DrumSynth {
  private cache = new Map<DrumSound, AudioBuffer>();
  private active = new Set<AudioBufferSourceNode>();

  constructor(private readonly ctx: AudioContext) {}

  private sound(kind: DrumSound): AudioBuffer {
    let buf = this.cache.get(kind);
    if (!buf) {
      switch (kind) {
        case 'kick': buf = makeKick(); break;
        case 'snare': buf = makeSnare(); break;
        case 'clap': buf = makeClap(); break;
        case 'chh': buf = makeHat(7500, 55, 0.12); break;
        case 'ohh': buf = makeHat(6500, 8, 0.45); break;
        case 'rim': buf = makeRim(); break;
        case 'tomL': buf = makeTom(150); break;
        case 'tomH': buf = makeTom(230); break;
      }
      this.cache.set(kind, buf);
    }
    return buf;
  }

  /**
   * Play a drum hit. `stopAt` optionally gates the hit (step length > 1),
   * cutting it off at an exact time.
   */
  trigger(kind: DrumSound, time: number, velocity: number, dest: AudioNode, stopAt?: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.sound(kind);
    const g = this.ctx.createGain();
    g.gain.value = Math.max(0.02, velocity / 127);
    src.connect(g);
    g.connect(dest);
    src.start(time);
    if (stopAt !== undefined && stopAt > time) {
      src.stop(stopAt);
    }
    this.active.add(src);
    src.onended = () => this.active.delete(src);
  }

  /** Cut off every in-flight hit (transport stop). */
  stopAll(): void {
    for (const src of this.active) {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    }
    this.active.clear();
  }
}